/* eslint-disable react-refresh/only-export-components */
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import {
  workflowService,
  type ApproveStepResponse,
  type SkipStepResponse,
  type RevisitStepResponse,
  type StepStructuredData,
  type QualityReport,
} from '../services/workflow';

// Define the 6 steps
export type WorkflowStep =
  | 'discovery'
  | 'design'
  | 'multiagent'
  | 'technical'
  | 'codegen'
  | 'operations';

export const WORKFLOW_STEPS: WorkflowStep[] = [
  'discovery',
  'design',
  'multiagent',
  'technical',
  'codegen',
  'operations',
];

export const STEP_LABELS: Record<WorkflowStep, string> = {
  discovery: 'Discovery',
  design: 'Design',
  multiagent: 'Multi-Agent',
  technical: 'Technical',
  codegen: 'Code Gen',
  operations: 'Operations',
};

// Code generation output type
export interface CodegenOutput {
  project_name: string;
  files: Record<string, string>;
  is_multi_agent: boolean;
}

// Chat message from step history
export interface ChatHistoryMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  file_ids?: string[];
}

// Step data loaded from backend
export interface StepData {
  status: StepState;
  chatHistory: ChatHistoryMessage[];
  specData: StepStructuredData | null;
  qualityScore?: number;
  qualityReport?: QualityReport;
  startedAt?: string;
  completedAt?: string;
}

// Step status for tracking approval/skip state
export type StepState = 'pending' | 'in_progress' | 'approved' | 'skipped';

// Initial empty step data
const emptyStepData: StepData = {
  status: 'pending',
  chatHistory: [],
  specData: null,
};

interface WorkflowState {
  currentStep: WorkflowStep;
  learningMode: boolean;
  projectId: string | null;
  projectTitle: string | null;
  codegenOutput: CodegenOutput | null;
  stepStatuses: Record<WorkflowStep, StepState>;
  stepDataMap: Record<WorkflowStep, StepData>;
  isLoading: boolean;
  error: string | null;
  workflowComplete: boolean;
  isDataLoaded: boolean;
}

interface WorkflowContextType extends WorkflowState {
  // Navigation
  setStep: (step: WorkflowStep) => void;
  nextStep: () => void;
  prevStep: () => void;

  // Settings
  toggleLearningMode: () => void;
  setProjectId: (id: string) => void;
  setCodegenOutput: (output: CodegenOutput | null) => void;

  // Step transition actions (with API calls)
  approveCurrentStep: () => Promise<ApproveStepResponse | null>;
  skipCurrentStep: (reason?: string) => Promise<SkipStepResponse | null>;
  revisitStep: (
    targetStep: WorkflowStep,
    clearSubsequent?: boolean
  ) => Promise<RevisitStepResponse | null>;

  // Status
  refreshProjectStatus: () => Promise<void>;
  loadFullProjectData: () => Promise<void>;
  clearError: () => void;
  canApprove: boolean;
  canSkip: boolean;
  canRevisit: (step: WorkflowStep) => boolean;

  // Step data accessors
  getStepData: (step: WorkflowStep) => StepData;
  updateStepData: (step: WorkflowStep, data: Partial<StepData>) => void;
}

const WorkflowContext = createContext<WorkflowContextType | undefined>(
  undefined
);

// Initial step statuses
const initialStepStatuses: Record<WorkflowStep, StepState> = {
  discovery: 'pending',
  design: 'pending',
  multiagent: 'pending',
  technical: 'pending',
  codegen: 'pending',
  operations: 'pending',
};

// Initial step data map
const initialStepDataMap: Record<WorkflowStep, StepData> = {
  discovery: { ...emptyStepData },
  design: { ...emptyStepData },
  multiagent: { ...emptyStepData },
  technical: { ...emptyStepData },
  codegen: { ...emptyStepData },
  operations: { ...emptyStepData },
};

export const WorkflowProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [currentStep, setCurrentStep] = useState<WorkflowStep>('discovery');
  const [learningMode, setLearningMode] = useState(false);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [projectTitle, setProjectTitle] = useState<string | null>(null);
  const [codegenOutput, setCodegenOutput] = useState<CodegenOutput | null>(
    null
  );
  const [stepStatuses, setStepStatuses] =
    useState<Record<WorkflowStep, StepState>>(initialStepStatuses);
  const [stepDataMap, setStepDataMap] =
    useState<Record<WorkflowStep, StepData>>(initialStepDataMap);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [workflowComplete, setWorkflowComplete] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // Basic navigation (local only)
  const setStep = useCallback((step: WorkflowStep) => {
    setCurrentStep(step);
  }, []);

  const toggleLearningMode = useCallback(() => {
    setLearningMode((prev) => !prev);
  }, []);

  const nextStep = useCallback(() => {
    const currentIndex = WORKFLOW_STEPS.indexOf(currentStep);
    if (currentIndex < WORKFLOW_STEPS.length - 1) {
      setCurrentStep(WORKFLOW_STEPS[currentIndex + 1]);
    }
  }, [currentStep]);

  const prevStep = useCallback(() => {
    const currentIndex = WORKFLOW_STEPS.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(WORKFLOW_STEPS[currentIndex - 1]);
    }
  }, [currentStep]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Check if we can perform actions
  const canApprove =
    !workflowComplete && currentStep !== ('completed' as WorkflowStep);
  const canSkip =
    !workflowComplete && currentStep !== ('completed' as WorkflowStep);

  const canRevisit = useCallback(
    (step: WorkflowStep): boolean => {
      const currentIndex = WORKFLOW_STEPS.indexOf(currentStep);
      const targetIndex = WORKFLOW_STEPS.indexOf(step);
      return targetIndex < currentIndex;
    },
    [currentStep]
  );

  // Approve current step (API call)
  const approveCurrentStep =
    useCallback(async (): Promise<ApproveStepResponse | null> => {
      if (!projectId) {
        setError('No project selected');
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await workflowService.approveStep(projectId);

        // Update local state
        setStepStatuses((prev) => ({
          ...prev,
          [currentStep]: 'approved',
        }));

        if (response.workflow_complete) {
          setWorkflowComplete(true);
        } else if (response.next_step) {
          setCurrentStep(response.next_step as WorkflowStep);
        }

        return response;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to approve step';
        setError(message);
        return null;
      } finally {
        setIsLoading(false);
      }
    }, [projectId, currentStep]);

  // Skip current step (API call)
  const skipCurrentStep = useCallback(
    async (reason?: string): Promise<SkipStepResponse | null> => {
      if (!projectId) {
        setError('No project selected');
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await workflowService.skipStep(projectId, reason);

        // Update local state
        setStepStatuses((prev) => ({
          ...prev,
          [currentStep]: 'skipped',
        }));

        if (response.workflow_complete) {
          setWorkflowComplete(true);
        } else if (response.next_step) {
          setCurrentStep(response.next_step as WorkflowStep);
        }

        return response;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to skip step';
        setError(message);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [projectId, currentStep]
  );

  // Revisit a previous step (API call)
  const revisitStep = useCallback(
    async (
      targetStep: WorkflowStep,
      clearSubsequent: boolean = false
    ): Promise<RevisitStepResponse | null> => {
      if (!projectId) {
        setError('No project selected');
        return null;
      }

      if (!canRevisit(targetStep)) {
        setError('Cannot revisit a step that is not before the current step');
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await workflowService.revisitStep(
          projectId,
          targetStep,
          clearSubsequent
        );

        // Update local state
        setCurrentStep(targetStep);

        if (clearSubsequent && response.cleared_steps) {
          setStepStatuses((prev) => {
            const updated = { ...prev };
            for (const step of response.cleared_steps) {
              if (step in updated) {
                updated[step as WorkflowStep] = 'pending';
              }
            }
            return updated;
          });
        }

        setWorkflowComplete(false);

        return response;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to revisit step';
        setError(message);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [projectId, canRevisit]
  );

  // Refresh project status from API (lightweight - just statuses)
  const refreshProjectStatus = useCallback(async (): Promise<void> => {
    if (!projectId) return;

    setIsLoading(true);
    try {
      const status = await workflowService.getProjectStatus(projectId);

      // Update step statuses
      const newStatuses: Record<WorkflowStep, StepState> = {
        ...initialStepStatuses,
      };
      for (const [step, stepStatus] of Object.entries(status.steps)) {
        if (step in newStatuses) {
          newStatuses[step as WorkflowStep] = stepStatus.status as StepState;
        }
      }
      setStepStatuses(newStatuses);

      // Update current step
      if (status.current_step === 'completed') {
        setWorkflowComplete(true);
      } else if (WORKFLOW_STEPS.includes(status.current_step as WorkflowStep)) {
        setCurrentStep(status.current_step as WorkflowStep);
      }

      // Update learning mode
      setLearningMode(status.learning_mode);
    } catch (err) {
      console.error('Failed to refresh project status:', err);
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  // Load full project data including chat history and spec data for all steps
  const loadFullProjectData = useCallback(async (): Promise<void> => {
    if (!projectId) return;

    setIsLoading(true);
    try {
      const project = await workflowService.getFullProject(projectId);

      // Update project title
      setProjectTitle(project.title);

      // Update current step
      if (project.current_step === 'completed') {
        setWorkflowComplete(true);
      } else if (
        WORKFLOW_STEPS.includes(project.current_step as WorkflowStep)
      ) {
        setCurrentStep(project.current_step as WorkflowStep);
      }

      // Update learning mode
      setLearningMode(project.learning_mode);

      // Map step field names to WorkflowStep
      const stepFieldMapping: Record<string, WorkflowStep> = {
        step1_discovery: 'discovery',
        step2_design: 'design',
        step3_multiagent: 'multiagent',
        step4_technical: 'technical',
        step5_codegen: 'codegen',
        step6_operations: 'operations',
      };

      // Update step statuses and step data
      const newStatuses: Record<WorkflowStep, StepState> = {
        ...initialStepStatuses,
      };
      const newStepDataMap: Record<WorkflowStep, StepData> = {
        ...initialStepDataMap,
      };

      for (const [fieldName, stepName] of Object.entries(stepFieldMapping)) {
        const stepData = project[fieldName as keyof typeof project] as
          | {
              status?: string;
              chat_history?: ChatHistoryMessage[];
              spec_data?: StepStructuredData;
              quality_score?: number;
              quality_report?: QualityReport;
              started_at?: string;
              completed_at?: string;
            }
          | undefined;

        if (stepData) {
          // Update status
          if (stepData.status) {
            newStatuses[stepName] = stepData.status as StepState;
          }

          // Update step data
          newStepDataMap[stepName] = {
            status: (stepData.status as StepState) || 'pending',
            chatHistory: stepData.chat_history || [],
            specData: stepData.spec_data || null,
            qualityScore: stepData.quality_score,
            qualityReport: stepData.quality_report,
            startedAt: stepData.started_at,
            completedAt: stepData.completed_at,
          };
        }
      }

      setStepStatuses(newStatuses);
      setStepDataMap(newStepDataMap);
      setIsDataLoaded(true);
    } catch (err) {
      console.error('Failed to load full project data:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to load project data'
      );
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  // Get step data for a specific step
  const getStepData = useCallback(
    (step: WorkflowStep): StepData => {
      return stepDataMap[step] || emptyStepData;
    },
    [stepDataMap]
  );

  // Update step data for a specific step (used when new data comes from chat)
  const updateStepData = useCallback(
    (step: WorkflowStep, data: Partial<StepData>) => {
      setStepDataMap((prev) => ({
        ...prev,
        [step]: {
          ...prev[step],
          ...data,
        },
      }));
    },
    []
  );

  const value: WorkflowContextType = {
    // State
    currentStep,
    learningMode,
    projectId,
    projectTitle,
    codegenOutput,
    stepStatuses,
    stepDataMap,
    isLoading,
    error,
    workflowComplete,
    isDataLoaded,

    // Navigation
    setStep,
    nextStep,
    prevStep,

    // Settings
    toggleLearningMode,
    setProjectId,
    setCodegenOutput,

    // Step transitions
    approveCurrentStep,
    skipCurrentStep,
    revisitStep,

    // Status
    refreshProjectStatus,
    loadFullProjectData,
    clearError,
    canApprove,
    canSkip,
    canRevisit,

    // Step data accessors
    getStepData,
    updateStepData,
  };

  return (
    <WorkflowContext.Provider value={value}>
      {children}
    </WorkflowContext.Provider>
  );
};

export const useWorkflow = () => {
  const context = useContext(WorkflowContext);
  if (context === undefined) {
    throw new Error('useWorkflow must be used within a WorkflowProvider');
  }
  return context;
};
